import React, { useState, useRef } from "react";
import {
  Container,
  Grid,
  Typography,
  Button,
  TextField as MuiTextField,
  Tab,
  Tabs,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import {
  CheckCircle,
  Edit as EditIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  SkipNext,
} from "@mui/icons-material";
import { Formik } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import { spacing } from "@mui/system";
import {
  useProvisionDevicesMutation,
  usePreviewProvisionMutation,
  useGetOrganizationsQuery,
} from "../../redux/slices/djangoApi";
import useAuth from "../../hooks/useAuth";
import { Navigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosConfig";

const TextField = styled(MuiTextField)(spacing);

const ProvisionDevices = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [provisionDevices, { isLoading: isProvisioning }] =
    useProvisionDevicesMutation();
  const [previewProvision, { isLoading: isPreviewing }] =
    usePreviewProvisionMutation();
  const { data: orgsData } = useGetOrganizationsQuery();
  const organizations = orgsData?.results || orgsData || [];

  const [results, setResults] = useState(null);
  const [preview, setPreview] = useState(null);
  const [csvRows, setCsvRows] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const fileInputRef = useRef(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");

  if (!user?.is_staff) {
    return <Navigate to="/devices" replace />;
  }

  const showSnack = (message, severity = "success") => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleCsvSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setCsvFile(file);
    setResults(null);
    setPreview(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split("\n").filter((l) => l.trim());
      if (lines.length < 2) {
        showSnack(
          "CSV must have a header row and at least one data row",
          "error"
        );
        return;
      }

      const parseCsvLine = (line) => {
        const values = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') {
            inQuotes = !inQuotes;
          } else if (ch === "," && !inQuotes) {
            values.push(current.trim());
            current = "";
          } else {
            current += ch;
          }
        }
        values.push(current.trim());
        return values;
      };

      const headers = parseCsvLine(lines[0]);
      const rows = lines.slice(1).map((line) => {
        const values = parseCsvLine(line);
        const row = {};
        headers.forEach((h, i) => {
          row[h] = values[i] || "";
        });
        return row;
      });

      setCsvRows(rows);
    };
    reader.readAsText(file);
  };

  const handlePreview = async () => {
    if (!csvRows.length) return;
    try {
      const result = await previewProvision(csvRows).unwrap();
      setPreview(result);
    } catch {
      showSnack("Failed to generate preview", "error");
    }
  };

  const handleBulkProvision = async () => {
    if (!csvRows.length) return;

    try {
      const result = await provisionDevices({
        devices: csvRows,
        overwrite,
      }).unwrap();
      setResults(result);
      setPreview(null);
      const created = result.created?.length || 0;
      const updated = result.updated?.length || 0;
      const skipped = result.skipped?.length || 0;
      const errors = result.errors?.length || 0;
      showSnack(
        `Provisioned: ${created} created, ${updated} updated, ${skipped} skipped, ${errors} errors`,
        errors > 0 ? "warning" : "success"
      );
    } catch {
      showSnack("Failed to provision devices", "error");
    }
  };

  const handleExportCsv = async () => {
    try {
      const response = await axiosInstance.get("devices/export_csv/", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "devices.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showSnack("Failed to export devices", "error");
    }
  };

  const handleSingleProvision = async (values, { resetForm }) => {
    try {
      const result = await provisionDevices({
        devices: [
          {
            serial_number: values.serialNumber,
            organization: values.organizationId,
            name: values.name || undefined,
            location: values.location || undefined,
          },
        ],
        overwrite,
      }).unwrap();

      setResults(result);
      const created = result.created?.length || 0;
      const updated = result.updated?.length || 0;
      if (created > 0) {
        showSnack(`Device ${values.serialNumber} provisioned`);
        resetForm();
      } else if (updated > 0) {
        showSnack(`Device ${values.serialNumber} updated`);
        resetForm();
      } else if (result.skipped?.length > 0) {
        showSnack(
          `Device ${values.serialNumber} already exists (enable overwrite to update)`,
          "info"
        );
      } else {
        showSnack(result.errors?.[0]?.error || "Provisioning failed", "error");
      }
    } catch {
      showSnack("Failed to provision device", "error");
    }
  };

  return (
    <Grid minH="100vh" p={4} mt={4} backgroundColor="white">
      <Container maxW="container.lg" p={8} rounded="lg" shadow="lg">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h1" gutterBottom>
            Provision Devices
          </Typography>
          <Button variant="outlined" onClick={handleExportCsv}>
            Export All Devices (CSV)
          </Button>
        </Box>
        <Typography variant="body2" color="textSecondary" mb={1}>
          Register new device serial numbers or update existing devices.
          Organization can be specified by ID or name.
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
            />
          }
          label="Overwrite existing devices (update name, location, organization)"
          sx={{ mb: 2 }}
        />

        <Snackbar
          open={snackOpen}
          autoHideDuration={4000}
          onClose={() => setSnackOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackOpen(false)}
            severity={snackSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackMessage}
          </Alert>
        </Snackbar>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Single Device" />
          <Tab label="Bulk CSV Upload" />
        </Tabs>

        {tab === 0 && (
          <Formik
            initialValues={{
              serialNumber: "",
              organizationId: "",
              name: "",
              location: "",
            }}
            validationSchema={Yup.object().shape({
              serialNumber: Yup.string().required("Serial number is required"),
              organizationId: Yup.string().required("Organization is required"),
            })}
            onSubmit={handleSingleProvision}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
            }) => (
              <form noValidate onSubmit={handleSubmit}>
                <TextField
                  name="serialNumber"
                  label="Serial Number"
                  placeholder="e.g. flipbit_001"
                  value={values.serialNumber}
                  error={Boolean(touched.serialNumber && errors.serialNumber)}
                  fullWidth
                  helperText={touched.serialNumber && errors.serialNumber}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                />
                <TextField
                  name="organizationId"
                  label="Organization"
                  select
                  value={values.organizationId}
                  error={Boolean(
                    touched.organizationId && errors.organizationId
                  )}
                  fullWidth
                  helperText={touched.organizationId && errors.organizationId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  name="name"
                  label="Device Name (optional)"
                  value={values.name}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                />
                <TextField
                  name="location"
                  label="Location (optional)"
                  value={values.location}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || isProvisioning}
                  sx={{ mt: 2 }}
                >
                  {isProvisioning ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Provision Device"
                  )}
                </Button>
              </form>
            )}
          </Formik>
        )}

        {tab === 1 && (
          <Box>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Upload a CSV with columns: <code>serial_number</code>,{" "}
              <code>organization</code> (name or ID), <code>name</code>{" "}
              (optional), <code>location</code> (optional), <code>lat</code>{" "}
              (optional), <code>lng</code> (optional),{" "}
              <code>enable_power_off</code> (optional)
            </Typography>
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2, mr: 2 }}
            >
              Select CSV File
            </Button>
            {csvFile && (
              <Typography variant="body2" component="span">
                {csvFile.name} ({csvRows.length} rows)
              </Typography>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleCsvSelect}
              style={{ display: "none" }}
            />

            {csvRows.length > 0 && !preview && (
              <>
                <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {Object.keys(csvRows[0]).map((key) => (
                          <TableCell key={key}>{key}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csvRows.slice(0, 20).map((row, i) => (
                        <TableRow key={i}>
                          {Object.values(row).map((val, j) => (
                            <TableCell key={j}>{val}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                      {csvRows.length > 20 && (
                        <TableRow>
                          <TableCell colSpan={Object.keys(csvRows[0]).length}>
                            ... and {csvRows.length - 20} more rows
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button
                  variant="contained"
                  onClick={handlePreview}
                  disabled={isPreviewing}
                  sx={{ mr: 2 }}
                >
                  {isPreviewing ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Review Changes"
                  )}
                </Button>
              </>
            )}

            {preview && <PreviewSummary preview={preview} />}

            {preview && (
              <Box mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => setPreview(null)}
                  sx={{ mr: 2 }}
                >
                  Back to CSV
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBulkProvision}
                  disabled={
                    isProvisioning ||
                    (preview.to_create.length === 0 &&
                      (!overwrite || preview.to_update.length === 0))
                  }
                >
                  {isProvisioning ? (
                    <CircularProgress size={24} />
                  ) : (
                    `Provision ${preview.to_create.length} new${
                      overwrite && preview.to_update.length > 0
                        ? `, update ${preview.to_update.length}`
                        : ""
                    }`
                  )}
                </Button>
                {!overwrite && preview.to_update.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 2 }}
                  >
                    Enable &quot;Overwrite&quot; above to update{" "}
                    {preview.to_update.length} existing device(s)
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {results && <ProvisionResults results={results} />}
      </Container>
    </Grid>
  );
};

const PreviewSummary = ({ preview }) => {
  const { to_create, to_update, errors } = preview;

  return (
    <Box mt={2}>
      <Typography variant="h6" gutterBottom>
        Preview Summary
      </Typography>

      <Box display="flex" gap={2} mb={3}>
        <Chip
          icon={<AddIcon />}
          label={`${to_create.length} to create`}
          color="success"
          variant="outlined"
        />
        <Chip
          icon={<EditIcon />}
          label={`${to_update.length} existing`}
          color="info"
          variant="outlined"
        />
        {errors.length > 0 && (
          <Chip
            icon={<ErrorIcon />}
            label={`${errors.length} errors`}
            color="error"
            variant="outlined"
          />
        )}
      </Box>

      {to_create.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ p: 2, bgcolor: "success.50" }}>
            <Typography variant="subtitle2" color="success.main">
              New Devices ({to_create.length})
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 200 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {to_create.map((d) => (
                  <TableRow key={d.serial_number}>
                    <TableCell>{d.serial_number}</TableCell>
                    <TableCell>{d.organization_name}</TableCell>
                    <TableCell>{d.name || "-"}</TableCell>
                    <TableCell>{d.location || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {to_update.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ p: 2, bgcolor: "info.50" }}>
            <Typography variant="subtitle2" color="info.main">
              Existing Devices ({to_update.length})
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 200 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Changes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {to_update.map((d) => (
                  <TableRow key={d.serial_number}>
                    <TableCell>{d.serial_number}</TableCell>
                    <TableCell>{d.organization_name}</TableCell>
                    <TableCell>
                      {Object.keys(d.changes).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No changes
                        </Typography>
                      ) : (
                        Object.entries(d.changes).map(([field, diff]) => (
                          <Chip
                            key={field}
                            label={`${field}: ${diff.from || "(empty)"} → ${
                              diff.to
                            }`}
                            size="small"
                            color="warning"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {errors.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ p: 2, bgcolor: "error.50" }}>
            <Typography variant="subtitle2" color="error.main">
              Errors ({errors.length})
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((err, i) => (
                  <TableRow key={i}>
                    <TableCell>{err.serial_number}</TableCell>
                    <TableCell>{err.error}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

const ProvisionResults = ({ results }) => (
  <Box mt={4}>
    <Divider sx={{ mb: 3 }} />
    <Typography variant="h6" gutterBottom>
      Results
    </Typography>
    {results.created?.length > 0 && (
      <Box mb={2}>
        <Typography variant="subtitle2" color="success.main">
          <CheckCircle
            fontSize="small"
            sx={{ verticalAlign: "middle", mr: 0.5 }}
          />
          Created ({results.created.length}):
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {results.created.map((sn) => (
            <Chip key={sn} label={sn} color="success" size="small" />
          ))}
        </Box>
      </Box>
    )}
    {results.updated?.length > 0 && (
      <Box mb={2}>
        <Typography variant="subtitle2" color="info.main">
          <EditIcon
            fontSize="small"
            sx={{ verticalAlign: "middle", mr: 0.5 }}
          />
          Updated ({results.updated.length}):
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {results.updated.map((sn) => (
            <Chip key={sn} label={sn} color="info" size="small" />
          ))}
        </Box>
      </Box>
    )}
    {results.skipped?.length > 0 && (
      <Box mb={2}>
        <Typography variant="subtitle2" color="text.secondary">
          <SkipNext
            fontSize="small"
            sx={{ verticalAlign: "middle", mr: 0.5 }}
          />
          Skipped ({results.skipped.length}):
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {results.skipped.map((sn) => (
            <Chip key={sn} label={sn} size="small" />
          ))}
        </Box>
      </Box>
    )}
    {results.errors?.length > 0 && (
      <Box mb={2}>
        <Typography variant="subtitle2" color="error.main">
          <ErrorIcon
            fontSize="small"
            sx={{ verticalAlign: "middle", mr: 0.5 }}
          />
          Errors ({results.errors.length}):
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Serial Number</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.errors.map((err, i) => (
                <TableRow key={i}>
                  <TableCell>{err.serial_number}</TableCell>
                  <TableCell>{err.error}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )}
  </Box>
);

export default ProvisionDevices;
